package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.network.api.UserApi
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.shared.data.local.NoOpTreeLocalDataSource
import fr.frux.shared.data.local.TreeLocalDataSource
import fr.frux.shared.data.mapper.toDomain
import fr.frux.shared.di.IoDispatcher
import fr.frux.shared.domain.model.BlockedUser
import fr.frux.shared.domain.model.UserProfile
import fr.frux.shared.domain.repository.UserRepository
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.withContext

/** Profil public (#227) et masquage (#515) : réseau seul, sauf l'éviction du cache des arbres. */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class UserRepositoryImpl(
    private val userApi: UserApi,
    @IoDispatcher private val dispatcher: CoroutineDispatcher,
    private val treeCache: TreeLocalDataSource = NoOpTreeLocalDataSource,
) : UserRepository {
    companion object {
        private const val TAG = "UserRepository"
    }

    private val changes = MutableSharedFlow<Unit>(extraBufferCapacity = 1)
    override val blockChanges: Flow<Unit> = changes.asSharedFlow()

    override suspend fun getUserProfile(id: Long): Result<UserProfile> =
        runCatchingCancellable { userApi.getUserProfile(id).toDomain() }
            .onFailure { Logger.e(TAG, "❌ Chargement du profil public $id échoué", it) }
            .mapFailureToAppError()

    override suspend fun blockUser(id: Long): Result<Unit> {
        val result =
            runCatchingCancellable { userApi.blockUser(id) }
                .onFailure { Logger.e(TAG, "❌ Masquage du compte $id échoué", it) }
                .mapFailureToAppError()
        if (result.isSuccess) {
            // ADR 0029 : pas de filtre persistant côté SQLDelight. Une éviction ratée n'annule pas
            // le masquage, la revalidation suivante le rattrape.
            runCatchingCancellable { withContext(dispatcher) { treeCache.deleteByCreator(id) } }
                .onFailure { Logger.e(TAG, "⚠️ Éviction du cache échouée (créateur $id)", it) }
            changes.tryEmit(Unit)
        }
        return result
    }

    override suspend fun unblockUser(id: Long): Result<Unit> {
        val result =
            runCatchingCancellable { userApi.unblockUser(id) }
                .onFailure { Logger.e(TAG, "❌ Démasquage du compte $id échoué", it) }
                .mapFailureToAppError()
        if (result.isSuccess) changes.tryEmit(Unit)
        return result
    }

    override suspend fun getBlockedUsers(): Result<List<BlockedUser>> =
        runCatchingCancellable { userApi.getBlockedUsers().map { it.toDomain() } }
            .onFailure { Logger.e(TAG, "❌ Chargement des comptes masqués échoué", it) }
            .mapFailureToAppError()

    override suspend fun reportUser(
        id: Long,
        reason: String,
        details: String?,
    ): Result<Unit> =
        runCatchingCancellable { userApi.reportUser(id, reason, details) }
            .onFailure { Logger.e(TAG, "❌ Signalement du compte $id échoué", it) }
            .mapFailureToAppError()
}
